import { useEffect, useReducer } from 'react';

import axios from 'axios';

import useLocalStorage from './useLocalStorage';
import { SCHOOL_INFO } from '../config/apiPaths';
import { getEnvironment } from '../utils/environment';
import { getUrls } from '../utils/url';

/**
 * School info with versioning. CURRENT_VERSION must
 * be updated if this type changes.
 */
export interface SchoolInfo {
    version: string;
    uuid: string;
    name: string;
    logo: string;
    prefix: string;
    type: OrganizationType;
    isManagedZoom: boolean;
    isTeams: boolean;
    isCollab: boolean;
}

export enum OrganizationType {
    /**
     * Zoom school
     */
    User = 'user',
    Collab = 'collab',
    Teams = 'teams'
}

interface SchoolInfoState {
    loading: boolean;
    error?: Error;
    data?: SchoolInfo;
}

const initialState = {
    loading: true,
    error: undefined,
    data: undefined
};

type Action = { type: 'loading' } | { type: 'success'; payload: SchoolInfo } | { type: 'error'; payload: Error };

const reducer = (state: SchoolInfoState, action: Action) => {
    switch (action.type) {
        case 'success':
            return { ...state, loading: false, data: action.payload };
        case 'error':
            return { ...state, loading: false, error: action.payload };
        case 'loading':
        default:
            return initialState;
    }
};

/**
 * Version for school info. When the SchoolInfo type changes,
 * this must be incremented to push the changes to those who
 * have local storage cached values.
 */
const CURRENT_VERSION = '1.0.2';

/**
 * Get basic school info and cache in local storage
 */
export const useSchoolInfo = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [school, setSchool] = useLocalStorage<SchoolInfo | undefined>('cfz_school', undefined);

    useEffect(() => {
        const { api } = getUrls();
        const { account } = getEnvironment();

        const url = `${api}${SCHOOL_INFO}`;

        if (school && (!school.version || school.version !== CURRENT_VERSION)) {
            setSchool(undefined);
        }

        if (school?.prefix && school?.prefix === account && school?.uuid) {
            dispatch({ type: 'success', payload: school });
            return;
        }

        const getSchool = async () => {
            dispatch({ type: 'loading' });

            try {
                const { data } = await axios.get(url);

                const schoolInfo = {
                    version: CURRENT_VERSION,
                    uuid: data.school_uuid,
                    name: data.name,
                    logo: data.logo,
                    prefix: data.prefix,
                    type: data.zoom_app_id,
                    isManagedZoom: data.zoom_app_id === OrganizationType.Collab,
                    isTeams: data.zoom_app_id === OrganizationType.Teams,
                    isCollab: data.is_collab
                };

                dispatch({ type: 'success', payload: schoolInfo });
                setSchool(schoolInfo);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);

                dispatch({ type: 'error', payload: err as Error });
            }
        };

        getSchool();
    }, [school, setSchool]);

    return { ...state };
};
